// src/pages/Authentication/UserProfile/index.tsx
// Main user profile page component

import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
// Styles
import '../../../assets/scss/components/UserProfile.scss';

// Components
import BreadCrumb from 'Components/Common/BreadCrumb';
import ProfileSidebar from './ProfileSidebar';
import PersonalInfoCard from './PersonalInfoCard';
import WorkInfoCard from './WorkInfoCard';
import AvatarUploadModal from './AvatarUploadModal';

// Types
import { UserProfile as IUserProfile, PersonalInfoFormValues } from './types';

// API
import { getUserProfile, updateUserProfile, getTenantById } from 'helpers/fakebackend_helper';

// Debug (chỉ dùng trong development)
import { debugAuthUser } from './debug';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('authentication');

const UserProfile: React.FC = () => {
  const [profile, setProfile] = useState<IUserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

    // Load profile on mount
  useEffect(() => {
    loadProfile();
    
    // Debug: Log authUser structure (chỉ trong development)
    if (process.env.NODE_ENV === 'development') {
      debugAuthUser();
    }
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getUserProfile();
      const data = response.data || response;

      // Backend trả Status dạng "Active"/"Locked" (không phải 'active'/'inactive' như FE đang so
      // sánh) — chuẩn hóa lại đây, nếu không trang sẽ luôn hiện "Không hoạt động" dù tài khoản đang
      // Active (mục 10.5, READE.md).
      const normalizedStatus: 'active' | 'inactive' =
        String(data.status || '').toLowerCase() === 'active' ? 'active' : 'inactive';

      let departmentName = data.departmentName;
      // User.DepartmentName chưa từng được backend populate (luôn null) — cũng như màn
      // /user-management, phải tự tra tên Tenant (đóng vai trò Phòng ban/Chi nhánh trong hệ thống
      // này) qua TenantId để hiển thị (mục 10.3, READE.md).
      if (!departmentName && data.tenantId) {
        try {
          const tenantResponse: any = await getTenantById(data.tenantId);
          const tenant = tenantResponse?.data || tenantResponse;
          departmentName = tenant?.name || '';
        } catch {
          // Không chặn hiển thị profile nếu tra cứu tenant thất bại — để trống là được.
        }
      }

      setProfile({
        ...data,
        status: normalizedStatus,
        departmentName: departmentName || '',
        // Chức vụ hiển thị lấy theo Vai trò (Role) của user (mục 10.3, READE.md)
        position: data.roleName || '',
      });
        } catch (error) {
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        const user = JSON.parse(authUser);
        
        // Mock profile data from authUser
        const mockProfile: IUserProfile = {
          id: user.uid || '1',
          email: user.email || user.data?.email || 'admin@themesbrand.com',
          name: user.username || user.data?.first_name || st('nguyen_quan_tri_text'),
          phoneNumber: user.data?.phone || '0912345678',
          idCard: user.data?.idCard || '291001234',
          dateOfBirth: user.data?.dateOfBirth || '1980-05-10',
          address: user.data?.address || st('hoan_kiem_ha_noi_text'),
          avatar: user.avatar || user.data?.avatar || '',
          
          // Work info
          departmentName: user.data?.department || st('ban_giam_doc_text'),
          position: user.data?.position || st('quan_tri_he_thong_text'),
          roleName: user.role || user.data?.role || st('admin_giam_doc_text'),
          lastModifiedAt: user.data?.lastModifiedAt || '01/01/2020',
          status: 'active',
        };
        
        setProfile(mockProfile);
      }
    }
  };

  const handleUpdateProfile = async (values: PersonalInfoFormValues) => {
    if (!profile?.id) {
      toast.error(st('khong_tim_thay_thong_tin_nguoi_message'));
      return;
    }
    
    setLoading(true);
    try {
      await updateUserProfile(profile.id, values);
      
      // Update local state
      if (profile) {
        setProfile({ ...profile, ...values });
      }
      
            // Update localStorage
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        const user = JSON.parse(authUser);
        user.data = { ...user.data, ...values };
        localStorage.setItem('authUser', JSON.stringify(user));
      }
      
      toast.success(st('cap_nhat_thong_tin_thanh_cong_message'));
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        st('co_loi_xay_ra_khi_cap_text');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUploadSuccess = (newAvatarUrl: string) => {
    if (profile) {
      setProfile({ ...profile, avatar: newAvatarUrl });
    }
    
        // Update localStorage
    const authUser = localStorage.getItem('authUser');
    if (authUser) {
      const user = JSON.parse(authUser);
      user.avatar = newAvatarUrl;
      localStorage.setItem('authUser', JSON.stringify(user));
    }
    
    toast.success(st('cap_nhat_anh_dai_dien_thanh_message'));
  };

  if (!profile) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{st('dang_tai_text')}</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = st('thong_tin_ca_nhan_lms_text');

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('thong_tin_ca_nhan_title')} pageTitle={st('tai_khoan_text')} />

          {/* Grid Layout: 300px sidebar + flexible content */}
          <Row>
            <Col lg={12}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '300px 1fr',
                  gap: '20px',
                }}
                className="profile-layout"
              >
                {/* Left Sidebar */}
                <ProfileSidebar
                  avatar={profile.avatar || ''}
                  userName={profile.name}
                  userRole={profile.position}
                  status={profile.status}
                  onChangeAvatar={() => setAvatarModalOpen(true)}
                />

                {/* Right Content */}
                <div>
                  {/* Personal Info Card */}
                  <PersonalInfoCard
                    email={profile.email}
                    initialValues={{
                      fullName: profile.name,
                      phone: profile.phoneNumber,
                      idCard: profile.idCard,
                      dateOfBirth: profile.dateOfBirth,
                      address: profile.address,
                    }}
                    loading={loading}
                    onSubmit={handleUpdateProfile}
                  />

                  {/* Work Info Card */}
                  <WorkInfoCard
                    department={profile.departmentName}
                    position={profile.position}
                    roleGroup={profile.roleName}
                    joinDate={profile.createdAt ? moment(profile.createdAt).format('DD/MM/YYYY') : '-'}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

            {/* Avatar Upload Modal */}
      <AvatarUploadModal
        isOpen={avatarModalOpen}
        toggle={() => setAvatarModalOpen(false)}
        currentAvatar={
          profile.avatar ||
          `https://ui-avatars.com/api/?name=${(profile.name || 'User').replace(/ /g, '+')}&background=0a2d54&color=fff&size=150&bold=true`
        }
        userName={profile.name}
        userId={profile.id}
        onUploadSuccess={handleAvatarUploadSuccess}
      />
    </React.Fragment>
  );
};

export default UserProfile;
